import { createContext, useContext, useEffect, useMemo } from "react";
import type React from "react";

import { AnalyticsBrowser } from "@segment/analytics-next";
import { graphql, useLazyLoadQuery } from "react-relay";
import Cookies from "universal-cookie";

import { getAuthCookieOptions } from "app/utils/auth";

import { useConfig } from "./ConfigContext";

import type { AnalyticsContextQuery } from "./__generated__/AnalyticsContextQuery.graphql";

interface Props {
  children: React.ReactNode;
}

// eslint-disable-next-line typescript/no-non-null-assertion
export const AnalyticsContext = createContext<AnalyticsBrowser>(undefined!);

export const AnalyticsProvider = ({ children }: Props) => {
  const config = useConfig();
  const data = useLazyLoadQuery<AnalyticsContextQuery>(
    graphql`
      query AnalyticsContextQuery($filterElement: FilterElementInput) {
        userData(filterElement: $filterElement) {
          edges {
            node {
              value
            }
          }
        }
      }
    `,
    {
      filterElement: {
        single: {
          name: "name",
          operator: "equals",
          value: "analytics_enabled",
        },
      },
    },
  );

  const analyticsEnabled = useMemo(
    () =>
      !(data.userData.edges ?? []).find((edge) => edge.node.value === "false"),
    [data.userData],
  );

  const segmentWriteKey = config?.segment_write_key;

  useEffect(() => {
    if (segmentWriteKey) {
      const cookies = new Cookies();
      cookies.set(
        "stacklet-segment-key",
        segmentWriteKey,
        getAuthCookieOptions(),
      );
    }
  }, [segmentWriteKey]);

  const analytics = useMemo(() => {
    // No write key (e.g. local-dev, which blanks it): a never-loaded
    // AnalyticsBrowser buffers calls without ever reaching Segment, so nothing
    // is sent and consumers' .track()/.page() stay no-ops.
    if (!segmentWriteKey) {
      return new AnalyticsBrowser();
    }
    return AnalyticsBrowser.load(
      { writeKey: segmentWriteKey },
      {
        integrations: {
          All: analyticsEnabled,
        },
      },
    );
  }, [analyticsEnabled, segmentWriteKey]);

  return (
    <AnalyticsContext.Provider value={analytics}>
      {children}
    </AnalyticsContext.Provider>
  );
};

export const useAnalytics = () => {
  const result = useContext(AnalyticsContext);

  if (!result) {
    throw new Error("AnalyticsContext used outside of its Provider!");
  }

  return result;
};
